// @flow
import axios from 'axios';
import { authInstance, restName } from '../firebase';
import { isProd } from './index';
import * as log from './log';

export const httpRequestWithToken = async ({
  url,
  method,
  payload,
  options,
}: {
  url: string,
  method: string,
  payload?: Object,
  options?: Object,
}): Object => {
  const token = await authInstance.currentUser.getIdToken();
  const opts = {
    ...options,
    auth: { username: restName, password: token },
  };

  return httpRequest({ url, method, payload, options: opts });
};

export const httpRequest = async ({
  url,
  method,
  payload,
  options,
}: {
  url: string,
  method: string,
  payload?: Object,
  options?: Object,
}): Object => {
  try {
    const res = await axios({
      url,
      method,
      data: payload,
      ...(options || {}),
    });

    log.log('apiRequest', 'info', {
      event: 'request url',
      url,
      method,
      payload,
      response: res.data,
      responseCode: res.status,
      success: true,
    });

    // monkey-patch response object for easier logging
    res.request.url = url;
    res.request.payload = payload;
    res.request.method = method;

    return res;
  } catch (err) {
    // Exception may be raised at different points of request lifecycle
    // and we want to monkey-patch the object the same as the
    // regular response above.

    if (err.response) {
      // Http request was made, but server replied with a non-2xx status code
      // (by default, axios raises an exception in such case).
      err.request = { url, method, payload };

      // Backend may send also specific error message embedded in the response json.
      // This can be formatted differently, depending on particular api implementation.
      const { data } = err.response;
      err.message = data.error?.error?.content?.message || data.msg || data.name || err.message;
    } else if (err.request) {
      // Http request was made, but no response was received
      err.request.url = url;
      err.request.payload = payload;
      err.request.method = method;
    } else {
      // Error happened during request setup or serialization
      err.request = { url, method, payload };
    }

    log.log('apiRequest', 'info', {
      event: 'request url',
      url,
      method,
      payload,
      response: err.response?.data,
      responseCode: err.response?.status,
      success: false,
    });

    throw err;
  }
};

export const getAvaBackendEndpoint = (): string =>
  localStorage.getItem('avaBackendUrl') || process.env.AVA_BACKEND_URL || '';

export const getScribeBackendEndpoint = (): string =>
  localStorage.getItem('scribeBackendUrl') || process.env.SCRIBE_BACKEND_URL || '';

export const firebaseFunctionsEndpoint = 'https://us-central1-ava-product.cloudfunctions.net/widgets';

export const cloudTranscriptsEndpoint = `${firebaseFunctionsEndpoint}/cloudTranscripts`;

export const getSubscriptionBackendEndpoint = (): string => process.env.SUBSCRIPTION_API_URL || '';

// TODO prod vs stage flags should be handled at build layer.
export const getSaasBackendEndpoint = (): string =>
  localStorage.getItem('saasBackendUrl') || (isProd() ? 'https://api.saas.ava.me' : process.env.SAAS_BACKEND_URL || '');

export const getSearchValueFromString = (searchString: string, key: string, _default: Object) => {
  if (!searchString) {
    return _default;
  }
  const params = new URLSearchParams(searchString);
  return params.get(key) || _default;
};

export const getSearchValue = (window: Object, key: string, _default: Object) => {
  // I don't know why, but window.location can sometimes be undefined. This looks
  // like browser bug, but even when window.location is undefined,
  // document.location is set.
  const location = window.location || document.location;
  const searchString = location.search;
  return getSearchValueFromString(searchString, key, _default);
};

export const setSearchValue = (window: Object, key: string, value: string) => {
  const location = window.location || document.location;
  const url = new URL(location.href);
  const params = new URLSearchParams(url.search);

  params.set(key, value);
  url.search = params.toString();
  window.history.replaceState(null, '', url.toString());
};

export const clearSearchValue = (window: Object, key: string) => {
  const location = window.location || document.location;
  const url = new URL(location.href);
  const params = new URLSearchParams(url.search);

  params.delete(key);
  url.search = params.toString();
  window.history.replaceState(null, '', url.toString());
};

const initBackend = getSearchValue(window, 'endpoint', undefined);
if (initBackend && initBackend.endsWith('ava.me')) {
  const url = new URL(initBackend);
  if (url.hostname.endsWith('ava.me')) {
    localStorage.setItem('avaBackendUrl', initBackend);
  }
}

export const backends = {
  production: {
    url: 'https://scribe.ava.me',
  },
  playground: {
    url: 'https://scribe-playground.ava.me',
  },
  local: {
    url: 'http://localhost:5000',
  },
};

export const updateBackend = (backendId: string) => {
  localStorage.setItem('scribeBackendUrl', backends[backendId].url);
  window.location.replace('/');
};

export const updateBackendAva = (backendId: string, backendsAva: Object) => {
  localStorage.setItem('avaBackendUrl', backendsAva[backendId].url);
  window.location.replace('/');
};
